import React from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import type {
  ProfitAndLossReportPayload,
  ProfitAndLossRow,
  ProfitAndLossSection,
  ProfitAndLossSummaryRow,
} from "src/api/reports/profit-and-loss";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { useTranslation } from "react-i18next";

const formatAmount = (value: number): string => {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPercent = (value: number): string => {
  return `${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

type Props = {
  data: ProfitAndLossReportPayload | undefined;
  isLoading: boolean;
};

const ProfitAndLossTable: React.FC<Props> = ({ data, isLoading }) => {
  const { t: generalT } = useTranslation("general");

  const columns = data?.columns ?? [];
  const sections = data?.sections ?? [];
  const summary = data?.summary ?? [];
  const totalColSpan = columns.length + 1;
  const isLastCol = (colIdx: number) => colIdx === columns.length - 1;

  // Collapsed parent-row keys. Rows arrive pre-ordered depth-first, so a row is
  // hidden when any ancestor (walked via parent_key) is collapsed.
  const [collapsed, setCollapsed] = React.useState<Set<string>>(new Set());

  const toggleRow = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // Start collapsed: seed every expandable row as collapsed whenever the report
  // (re)loads, so only top-level accounts show and sub-accounts are drilled into.
  React.useEffect(() => {
    const expandableKeys = new Set<string>();
    (data?.sections ?? []).forEach((section) =>
      section.rows.forEach((row) => {
        if (row.is_expandable) expandableKeys.add(row.key);
      }),
    );
    setCollapsed(expandableKeys);
  }, [data]);

  const visibleRows = (rows: ProfitAndLossRow[]): ProfitAndLossRow[] => {
    const byKey = new Map(rows.map((r) => [r.key, r]));
    return rows.filter((row) => {
      let parent = row.parent_key ?? null;
      while (parent) {
        if (collapsed.has(parent)) return false;
        parent = byKey.get(parent)?.parent_key ?? null;
      }
      return true;
    });
  };

  const sectionHeaderCellSx = {
    bgcolor: "primary.light",
    py: 1.25,
    px: 2,
  };

  const groupHeaderRowSx = {
    bgcolor: "#F9FAFB",
    transition: "background-color 0.15s ease",
    "&:hover": {
      bgcolor: "#F2F4F7",
    },
    "& td": {
      borderBottom: "1px solid #EAECF0",
      py: 0.875,
      px: 2,
      pl: 3,
    },
  };

  const renderAmountCells = (
    amounts: number[],
    isBold: boolean,
    rowType: string,
  ) =>
    columns.map((col, colIdx) => {
      const val = amounts[colIdx] ?? 0;
      const isTotal = isLastCol(colIdx);
      const formatted =
        rowType === "percent" ? formatPercent(val) : formatAmount(val);

      return (
        <TableCell
          key={col.branch_id ?? "total"}
          align="right"
          sx={{
            fontWeight: isBold ? 700 : 400,
            color: isTotal ? "#101828" : "#344054",
            fontSize: "0.8125rem",
          }}
        >
          {formatted}
        </TableCell>
      );
    });

  const renderSectionHeader = (section: ProfitAndLossSection) => (
    <TableRow key={`section-${section.key}`}>
      <TableCell colSpan={totalColSpan} sx={sectionHeaderCellSx}>
        <Typography
          fontWeight={700}
          fontSize="0.8125rem"
          color="primary.dark"
          textTransform="uppercase"
          letterSpacing="0.03em"
        >
          {section.title}
        </Typography>
      </TableCell>
    </TableRow>
  );

  const renderRow = (row: ProfitAndLossRow) => {
    if (row.row_type === "header") {
      return (
        <TableRow key={row.key} hover sx={groupHeaderRowSx}>
          <TableCell colSpan={totalColSpan}>
            <Typography
              fontWeight={600}
              fontSize="0.8125rem"
              color="#344054"
            >
              {row.label}
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    const depth = row.depth ?? 0;
    const isAccountRow = row.depth != null;
    const isExpandable = !!row.is_expandable;
    const isCollapsed = collapsed.has(row.key);

    return (
      <TableRow key={row.key} hover>
        <TableCell
          sx={{
            fontWeight: row.is_bold ? 700 : 400,
            fontSize: "0.875rem",
            pl: 2 + depth * 3,
          }}
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            {isExpandable ? (
              <IconButton
                size="small"
                onClick={() => toggleRow(row.key)}
                sx={{ p: 0.25, ml: -0.75 }}
                aria-label={isCollapsed ? "expand row" : "collapse row"}
              >
                {isCollapsed ? (
                  <KeyboardArrowRightIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </IconButton>
            ) : (
              // Align non-expandable account rows with their expandable siblings.
              isAccountRow && <Box width={20} flexShrink={0} />
            )}
            <span>{row.label}</span>
          </Box>
        </TableCell>
        {renderAmountCells(row.amounts, row.is_bold, row.row_type)}
      </TableRow>
    );
  };

  const renderSummaryRow = (row: ProfitAndLossSummaryRow) => (
    <TableRow key={row.key} hover>
      <TableCell
        sx={{
          fontWeight: 700,
          fontSize: "0.875rem",
          pl: 2,
        }}
      >
        {row.label}
      </TableCell>
      {renderAmountCells(row.amounts, true, row.row_type)}
    </TableRow>
  );

  const headerLabels = [
    generalT("account"),
    ...columns.map((col) => col.name),
  ];

  return (
    <Table>
      <TableHead>
        <TableRow sx={{ bgcolor: "primary.main" }}>
          {headerLabels.map((label, idx) => (
            <TableCell key={label} align={idx === 0 ? "left" : "right"}>
              <Typography
                fontSize="12px"
                textTransform="uppercase"
                color="#fff"
                lineHeight="18px"
                fontWeight={500}
              >
                {label}
              </Typography>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      {isLoading ? (
        <TableLoadingSkeleton
          rowsLength={8}
          cellsLength={headerLabels.length}
        />
      ) : (
        <TableBody>
          {sections.map((section) => [
            renderSectionHeader(section),
            ...visibleRows(section.rows).map(renderRow),
          ])}
          {summary.length > 0 && (
            <TableRow key="summary-header">
              <TableCell colSpan={totalColSpan} sx={sectionHeaderCellSx}>
                <Typography
                  fontWeight={700}
                  fontSize="0.8125rem"
                  color="primary.dark"
                  textTransform="uppercase"
                  letterSpacing="0.03em"
                >
                  {generalT("summary")}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {summary.map(renderSummaryRow)}
        </TableBody>
      )}
    </Table>
  );
};

export default ProfitAndLossTable;
